import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, Bell } from "lucide-react";
import { LANG_META, STATUS_META, SLA_META, metaLabel, fmtDuration } from "@/lib/supportUi";
import { TicketDrawer } from "@/components/shared/TicketDrawer";
import { useSupportNotifications } from "@/hooks/useSupportNotifications";
import { useContent } from "@/context/ContentContext";

const BASE = "/agent/support";

export default function AgentSupport() {
  const { t: tr } = useContent();
  const [queues, setQueues] = useState(null);
  const [langs, setLangs] = useState([]);
  const [filter, setFilter] = useState({ language: "", status: "", assigned: "" });
  const [rows, setRows] = useState(null);
  const [agents, setAgents] = useState([]);
  const [openId, setOpenId] = useState(() => new URLSearchParams(window.location.search).get("t"));
  const { unread, markRead } = useSupportNotifications(BASE);
  const [newIds, setNewIds] = useState(new Set());
  const loadNewIds = useCallback(() => api.get(`${BASE}/notifications`).then((r) => {
    const s = new Set((r.data.notifications || []).filter((n) => !n.read && ["transferred", "reassigned", "language_change"].includes(n.type)).map((n) => n.ticket_id));
    setNewIds(s);
  }).catch(() => {}), []);
  useEffect(() => { loadNewIds(); const iv = setInterval(loadNewIds, 12000); return () => clearInterval(iv); }, [loadNewIds]);

  const loadQueues = useCallback(() => api.get(`${BASE}/queues`).then((r) => { setQueues(r.data.queues); setLangs(r.data.allowed_languages || []); }).catch(() => setQueues([])), []);
  const loadRows = useCallback(() => {
    const p = new URLSearchParams();
    if (filter.language) p.set("language", filter.language);
    if (filter.status) p.set("status", filter.status);
    if (filter.assigned) p.set("assigned", filter.assigned);
    return api.get(`${BASE}/tickets?${p}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [filter]);

  useEffect(() => { loadQueues(); api.get(`${BASE}/agents`).then((r) => setAgents(r.data)).catch(() => {}); }, [loadQueues]);
  useEffect(() => { loadRows(); const iv = setInterval(loadRows, 15000); return () => clearInterval(iv); }, [loadRows]);

  const refresh = () => { loadRows(); loadQueues(); };

  if (queues === null) return <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-accent" /></div>;

  if (!langs.length) return (
    <div data-testid="agent-support" className="rounded-xl border border-amber-700/40 bg-amber-500/10 p-6 text-sm text-amber-300">
      {tr("agentsup.no_lang", "Aucune langue de support ne vous est attribuée. Contactez un administrateur pour accéder aux files de tickets.")}
    </div>
  );

  return (
    <div data-testid="agent-support">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-white">{tr("agentsup.title", "Support · Tickets")}</h1>
        <button onClick={markRead} data-testid="agent-notif-bell" className="relative rounded-full border border-slate-700 p-2 hover:bg-slate-800">
          <Bell className="h-5 w-5 text-slate-300" />
          {unread > 0 && <span data-testid="agent-notif-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
        </button>
      </div>

      {/* language queues */}
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {queues.map((q) => (
          <button key={q.language} onClick={() => setFilter((f) => ({ ...f, language: f.language === q.language ? "" : q.language }))}
            data-testid={`queue-${q.language}`}
            className={`rounded-xl border p-4 text-left transition-colors ${filter.language === q.language ? "border-accent bg-accent/10" : "border-slate-800 bg-slate-900 hover:bg-slate-800/50"}`}>
            <div className="text-sm font-semibold text-white">{LANG_META[q.language]?.flag} {q.label}</div>
            <div className="mt-1 text-xs text-slate-400">{q.open} {tr("agentsup.open", "ouverts")} · {q.mine} {tr("agentsup.mine", "à moi")}</div>
          </button>
        ))}
      </div>

      {/* filters */}
      <div className="mb-3 flex flex-wrap gap-2">
        {[["", tr("adminsup.f.all", "Tous")], ["waiting", tr("support.status.waiting", "En attente")], ["in_progress", tr("support.status.in_progress", "En cours")], ["waiting_client", tr("support.status.waiting_client", "Attente client")], ["waiting_internal", tr("support.status.waiting_internal", "Attente interne")], ["resolved", tr("adminsup.f.resolved", "Résolus")], ["closed", tr("support.status.closed", "Fermés")]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter((f) => ({ ...f, status: v }))} data-testid={`filter-status-${v || "all"}`}
            className={`rounded-full px-3 py-1.5 text-xs ${filter.status === v ? "bg-accent text-white" : "border border-slate-700 text-slate-300"}`}>{l}</button>
        ))}
        <button onClick={() => setFilter((f) => ({ ...f, assigned: f.assigned === "me" ? "" : "me" }))} data-testid="filter-mine"
          className={`rounded-full px-3 py-1.5 text-xs ${filter.assigned === "me" ? "bg-accent text-white" : "border border-slate-700 text-slate-300"}`}>{tr("agentsup.my_tickets", "Mes tickets")}</button>
      </div>

      {/* list */}
      {rows === null ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div> : (
        <div className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900">
          {rows.map((t) => (
            <button key={t.id} onClick={() => setOpenId(t.id)} data-testid={`ticket-${t.id}`} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/50">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">{t.subject}</div>
                <div className="text-xs text-slate-500">{t.user_name} · {t.user_email}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {newIds.has(t.id) && <span data-testid={`new-transfer-${t.id}`} className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">{tr("agentsup.new_transfer", "Nouveau transfert")}</span>}
                <span className={`rounded-full border px-2 py-0.5 text-[10px] ${LANG_META[t.language]?.color}`}>{LANG_META[t.language]?.flag}</span>
                <span className={`rounded px-2 py-0.5 text-[10px] ${STATUS_META[t.status]?.color}`}>{metaLabel(STATUS_META[t.status], tr)}</span>
                {t.sla && (t.sla.status === "due_soon" || t.sla.status === "breached") && <span className={`rounded-full border px-2 py-0.5 text-[10px] ${SLA_META[t.sla.status]?.color}`}>{metaLabel(SLA_META[t.sla.status], tr)}</span>}
                {t.unread_staff > 0 && <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{t.unread_staff}</span>}
              </div>
            </button>
          ))}
          {!rows.length && <div className="px-4 py-10 text-center text-sm text-slate-500">Aucun ticket dans cette file.</div>}
        </div>
      )}

      {openId && <TicketDrawer id={openId} base={BASE} isAdmin={false} agents={agents} onChanged={refresh} onClose={() => { setOpenId(null); refresh(); markRead(); }} />}
    </div>
  );
}
